/* Progressive enhancements only. Product/variant links remain server-rendered. */
class LbCampaign extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.toggle = this.querySelector('[data-film-toggle]');
    this.stage = this.querySelector('[data-film-stage]');
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.mobile = matchMedia('(max-width: 749px)');
    this.connection = navigator.connection;
    this.inView = false;
    this.wantsPlay = this.dataset.autoplay === 'true' && !this.reduced.matches && !this.connection?.saveData;
    if (!this.toggle || !this.stage || !this.querySelector('template[data-film]')) return;
    this.toggle.hidden = false;
    this.toggle.addEventListener('click', () => {
      this.wantsPlay = !this.wantsPlay;
      this.syncPlayback();
    }, options);
    document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
    this.reduced.addEventListener('change', () => {
      if (this.reduced.matches) this.wantsPlay = false;
      this.syncPlayback();
    }, options);
    this.connection?.addEventListener('change', () => {
      if (this.connection.saveData) this.wantsPlay = false;
      this.syncPlayback();
    }, options);
    this.mobile.addEventListener('change', () => {
      this.disposeVideo();
      this.syncPlayback();
    }, options);
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.15;
        this.syncPlayback();
      }, { threshold: [0, 0.15] });
      this.observer.observe(this);
    } else {
      this.inView = true;
      this.syncPlayback();
    }
  }

  loadVideo() {
    if (this.video) return this.video;
    const mobile = this.mobile.matches && this.querySelector('template[data-film="mobile"]');
    const template = mobile || this.querySelector('template[data-film="desktop"]');
    if (!template) return null;
    this.stage.append(template.content.cloneNode(true));
    this.video = this.stage.querySelector('video');
    if (!this.video) return null;
    this.video.muted = true;
    this.video.playsInline = true;
    const options = { signal: this.abort.signal };
    this.video.addEventListener('playing', () => {
      this.stage.classList.add('has-frame');
      this.updateToggle();
    }, options);
    this.video.addEventListener('pause', () => this.updateToggle(), options);
    this.video.addEventListener('error', () => {
      this.wantsPlay = false;
      this.stage.classList.remove('has-frame');
      this.toggle.hidden = true;
      this.video?.pause();
    }, options);
    return this.video;
  }

  async syncPlayback() {
    if (!this.isConnected) return;
    const request = this.playRequest = (this.playRequest || 0) + 1;
    if (!this.wantsPlay || !this.inView || document.hidden) {
      this.video?.pause();
      this.updateToggle();
      return;
    }
    const video = this.loadVideo();
    if (!video) return;
    try {
      await video.play();
      // A visibility change or user pause can happen while play() is pending.
      if (!this.isConnected || !this.wantsPlay || !this.inView || document.hidden || video !== this.video) video.pause();
    } catch {
      if (video === this.video && request === this.playRequest) {
        this.wantsPlay = false;
        this.stage.classList.remove('has-frame');
      }
    }
    this.updateToggle();
  }

  updateToggle() {
    if (!this.toggle) return;
    const playing = this.video && !this.video.paused;
    this.toggle.textContent = playing ? this.toggle.dataset.pauseLabel : this.toggle.dataset.playLabel;
    const playLabel = this.toggle.dataset.playAriaLabel || 'Play campaign film';
    const pauseLabel = this.toggle.dataset.pauseAriaLabel || 'Pause campaign film';
    this.toggle.setAttribute('aria-label', playing ? pauseLabel : playLabel);
  }

  disposeVideo() {
    this.video?.pause();
    this.video?.remove();
    this.video = null;
    this.stage?.classList.remove('has-frame');
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.disposeVideo();
    this.abort = null;
  }
}

class LbProductRail extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.track = this.querySelector('[data-rail-track]');
    this.controls = this.querySelector('[data-rail-controls]');
    if (!this.track || !this.controls) return;
    this.previous = this.querySelector('[data-rail-previous]');
    this.next = this.querySelector('[data-rail-next]');
    this.previous.addEventListener('click', () => this.move(-1), options);
    this.next.addEventListener('click', () => this.move(1), options);
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }, options);
    this.track.addEventListener('keydown', (event) => {
      if (event.target !== this.track) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        this.move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
      }
    }, options);
    if ('ResizeObserver' in window) {
      this.resize = new ResizeObserver(() => this.update());
      this.resize.observe(this.track);
    } else window.addEventListener('resize', () => this.update(), options);
    this.update();
  }

  move(direction) {
    const card = this.track.firstElementChild;
    if (!card) return;
    const style = getComputedStyle(this.track);
    const gap = parseFloat(style.columnGap) || 0;
    const delta = card.getBoundingClientRect().width + gap;
    this.track.scrollBy({
      left: delta * direction * (style.direction === 'rtl' ? -1 : 1),
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  update() {
    const first = this.track.firstElementChild;
    const last = this.track.lastElementChild;
    const overflow = this.track.scrollWidth > this.track.clientWidth + 2;
    this.controls.hidden = !overflow;
    if (!first || !last) return;
    const frame = this.track.getBoundingClientRect();
    const rtl = getComputedStyle(this.track).direction === 'rtl';
    this.previous.disabled = rtl ? first.getBoundingClientRect().right <= frame.right + 2 : first.getBoundingClientRect().left >= frame.left - 2;
    this.next.disabled = rtl ? last.getBoundingClientRect().left >= frame.left - 2 : last.getBoundingClientRect().right <= frame.right + 2;
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resize?.disconnect();
    cancelAnimationFrame(this.frame);
    this.abort = null;
  }
}

class LbLayerStory extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.tabs = [...this.querySelectorAll('[data-color-tab]')];
    this.panels = [...this.querySelectorAll('[data-color-panel]')];
    if (!this.tabs.length) return;
    this.querySelector('[data-color-tabs]').hidden = false;
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index), options);
      tab.addEventListener('keydown', (event) => {
        let next;
        const rtl = getComputedStyle(this).direction === 'rtl';
        if (event.key === 'ArrowRight') next = index + (rtl ? -1 : 1);
        if (event.key === 'ArrowLeft') next = index + (rtl ? 1 : -1);
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        next = (next + this.tabs.length) % this.tabs.length;
        this.select(next);
        this.tabs[next].focus();
      }, options);
    });
    this.addEventListener('shopify:block:select', (event) => {
      const index = this.panels.findIndex((panel) => panel.dataset.blockId === event.detail.blockId);
      if (index >= 0) this.select(index);
    }, options);
    this.select(0);
  }

  select(index) {
    this.tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', String(i === index));
      tab.tabIndex = i === index ? 0 : -1;
      this.panels[i].hidden = i !== index;
    });
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.abort = null;
  }
}

for (const [name, component] of [['lb-campaign', LbCampaign], ['lb-product-rail', LbProductRail], ['lb-layer-story', LbLayerStory]]) {
  if (!customElements.get(name)) customElements.define(name, component);
}
